import { AnyAction } from 'redux'

// Action Types
export const ADD_QUESTION = 'ADD_QUESTION'
export const DELETE_QUESTION = 'DELETE_QUESTION'
export const UPDATE_QUESTION = 'UPDATE_QUESTION'
export const SET_SECTION_OPTIONS = 'SET_SECTION_OPTIONS'
export const ADD_SECTION = 'ADD_SECTION'
export const DELETE_SECTION = 'DELETE_SECTION'
export const SET_EXPANDED_SECTION = 'SET_EXPANDED_SECTION'
export const SET_IS_FETCHING_SECTIONS = 'SET_IS_FETCHING_SECTIONS'
export const RESET_QUESTIONS = 'RESET_QUESTIONS'
export const RESTORE_QUESTIONS_STATE = 'RESTORE_QUESTIONS_STATE'

// Type definitions
export interface QuestionOption {
  id: string
  value: string
}

export interface QuestionData {
  id: string
  question: string
  type: string // Changed to string to be compatible with QuestionItem
  isRequired: boolean
  options?: QuestionOption[] // Made optional to be compatible with QuestionItem
  correctAnswers?: string[] // Made optional to be compatible with QuestionItem
  minCorrectOptions?: number // Minimum number of correct options that must be selected
  sectionId?: string
}

export interface SectionOption {
  value: number | string
  label: string
}

export interface QuestionsState {
  // Map of sectionId to array of questions for that section
  questionsBySection: Record<string, QuestionData[]>
  sections: SectionOption[]
  sectionOptions: SectionOption[]
  expandedSection: string | null
  isFetchingSections: boolean
}

// Initial State
const initialState: QuestionsState = {
  questionsBySection: {},
  sections: [],
  sectionOptions: [],
  expandedSection: null,
  isFetchingSections: false,
}

// Action Creators
export const addQuestion = (
  question: QuestionData,
  sectionId: string,
): { type: string; payload: { question: QuestionData; sectionId: string } } => ({
  type: ADD_QUESTION,
  payload: { question, sectionId },
})

export const deleteQuestion = (
  questionId: string,
  sectionId: string,
): { type: string; payload: { questionId: string; sectionId: string } } => ({
  type: DELETE_QUESTION,
  payload: { questionId, sectionId },
})

export const updateQuestion = (
  question: QuestionData,
  sectionId: string,
): { type: string; payload: { question: QuestionData; sectionId: string } } => ({
  type: UPDATE_QUESTION,
  payload: { question, sectionId },
})

export const setSectionOptions = (options: SectionOption[]): { type: string; payload: SectionOption[] } => ({
  type: SET_SECTION_OPTIONS,
  payload: options,
})

export const addSection = (section: SectionOption): { type: string; payload: SectionOption } => ({
  type: ADD_SECTION,
  payload: section,
})

export const deleteSection = (sectionIndex: number): { type: string; payload: number } => ({
  type: DELETE_SECTION,
  payload: sectionIndex,
})

export const setExpandedSection = (sectionId: string | null): { type: string; payload: string | null } => ({
  type: SET_EXPANDED_SECTION,
  payload: sectionId,
})

export const setIsFetchingSections = (isFetching: boolean): { type: string; payload: boolean } => ({
  type: SET_IS_FETCHING_SECTIONS,
  payload: isFetching,
})

export const resetQuestions = (): { type: string } => ({
  type: RESET_QUESTIONS,
})

export const restoreQuestionsState = (payload: {
  sections: SectionOption[]
  questionsBySection: Record<string, QuestionData[]>
}): { type: string; payload: { sections: SectionOption[]; questionsBySection: Record<string, QuestionData[]> } } => ({
  type: RESTORE_QUESTIONS_STATE,
  payload,
})

// Reducer
const questionsReducer = (state = initialState, action: AnyAction): QuestionsState => {
  switch (action.type) {
    case ADD_QUESTION: {
      const { question, sectionId } = action.payload
      const sectionQuestions = state.questionsBySection[sectionId] || []
      return {
        ...state,
        questionsBySection: {
          ...state.questionsBySection,
          [sectionId]: [...sectionQuestions, question],
        },
      }
    }

    case DELETE_QUESTION: {
      const { questionId, sectionId } = action.payload
      const sectionQuestions = state.questionsBySection[sectionId] || []
      return {
        ...state,
        questionsBySection: {
          ...state.questionsBySection,
          [sectionId]: sectionQuestions.filter(question => question.id !== questionId),
        },
      }
    }

    case UPDATE_QUESTION: {
      const { question, sectionId } = action.payload
      const sectionQuestions = state.questionsBySection[sectionId] || []
      return {
        ...state,
        questionsBySection: {
          ...state.questionsBySection,
          [sectionId]: sectionQuestions.map(q => {
            if (q.id === question.id) {
              return { ...q, ...question }
            }
            return q
          }),
        },
      }
    }

    case SET_SECTION_OPTIONS:
      return {
        ...state,
        sectionOptions: action.payload,
      }

    case ADD_SECTION: {
      // Check if we're receiving the section directly or as part of a payload object
      const isCustomPayload = action.payload?.section && action.payload?.skipDefaultQuestion !== undefined
      const section = isCustomPayload ? action.payload.section : action.payload
      const skipDefaultQuestion = isCustomPayload ? action.payload.skipDefaultQuestion : false

      const sectionId = `section-${state.sections.length}-${section.value}`

      // Create a default question for the new section (only if not skipped)
      const defaultQuestion = {
        id: `question_1_${Date.now()}`,
        question: '',
        type: 'free_text',
        isRequired: true,
        options: [
          { id: `option_1_${Date.now()}`, value: '' },
          { id: `option_2_${Date.now()}`, value: '' },
        ],
        correctAnswers: [],
        minCorrectOptions: 1, // Default to requiring at least 1 correct option
      }

      return {
        ...state,
        sections: [...state.sections, section],
        questionsBySection: {
          ...state.questionsBySection,
          // Only add the default question if not skipped
          [sectionId]: skipDefaultQuestion ? [] : [defaultQuestion],
        },
      }
    }

    case DELETE_SECTION: {
      const sectionIndex = action.payload
      const updatedSections = [...state.sections]
      const removedSection = updatedSections[sectionIndex]
      const sectionId = `section-${sectionIndex}-${removedSection.value}`

      // Remove the section from the array
      updatedSections.splice(sectionIndex, 1)

      // Create a new questionsBySection object with updated keys
      const updatedQuestionsBySection: Record<string, QuestionData[]> = {}

      // Copy questions for sections before the deleted section (unchanged)
      Object.keys(state.questionsBySection).forEach(key => {
        // Extract the index from the section ID (format: section-{index}-{value})
        const keyParts = key.split('-')
        const keyIndex = parseInt(keyParts[1], 10)

        if (keyIndex < sectionIndex) {
          // Keep sections before the deleted one unchanged
          updatedQuestionsBySection[key] = state.questionsBySection[key]
        } else if (keyIndex > sectionIndex) {
          // For sections after the deleted one, update their section IDs
          // to reflect their new indices (shifted down by 1)
          const newIndex = keyIndex - 1
          const sectionValue = updatedSections[newIndex].value
          const newKey = `section-${newIndex}-${sectionValue}`
          updatedQuestionsBySection[newKey] = state.questionsBySection[key]
        }
        // Skip the deleted section (keyIndex === sectionIndex)
      })

      return {
        ...state,
        sections: updatedSections,
        questionsBySection: updatedQuestionsBySection,
      }
    }

    case SET_EXPANDED_SECTION:
      return {
        ...state,
        expandedSection: action.payload,
      }

    case SET_IS_FETCHING_SECTIONS:
      return {
        ...state,
        isFetchingSections: action.payload,
      }

    case RESET_QUESTIONS:
      return initialState

    case RESTORE_QUESTIONS_STATE:
      return {
        ...state,
        sections: action.payload.sections,
        questionsBySection: action.payload.questionsBySection,
      }

    default:
      return state
  }
}

export default questionsReducer
